import { Close, Delete, EditNote, UploadFileOutlined } from "@mui/icons-material";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";

import { patch } from "../../../../../api/api";
import { exportProfiles } from "../../../../../api/profile";
import { getExternalUserTypesDisplay, getInternalUserTypesDisplay } from "../../../../../utils/userRoleUtils";
import UsersBulkEditDialog from "../../dialogs/users/usersBulkEditDialog";
import { assertSectionControllerContract } from "../../../../shared/management/sections/contracts/sectionControllerContract";
import RowActionsMenu from "../../../../shared/management/sections/shared/table/RowActionsMenu";
import { createManagementCellRenderer } from "../../../../shared/management/sections/shared/table/createManagementCellRenderer";
import CreatedByFilterPopover from "../../../../shared/management/sections/users/table/CreatedByFilterPopover";
import UserTypeSelectPopover from "../../../../shared/management/sections/users/table/UserTypeSelectPopover";
import ConfirmDeleteDialog from "../../../../shared/management/shared/ConfirmDeleteDialog";
import useUsersTabData from "./useUsersTabData";

export function useUsersSectionController({
  columns,
  rowsPerPage,
  sortConfig,
  onSortChange,
  selectedRows,
  setSelectedRows,
  openDialogManager,
  notify,
}) {
  const usersTabData = useUsersTabData({ rowsPerPage, sortConfig });

  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deletingRow, setDeletingRow] = useState(false);

  const { renderCellContent } = useMemo(
    () =>
      createManagementCellRenderer({
        onNoteClick: undefined,
      }),
    []
  );

  const internalUserTypes = useMemo(() => getInternalUserTypesDisplay(), []);
  const externalUserTypes = useMemo(() => getExternalUserTypesDisplay(), []);

  const filterPopoverByField = useMemo(
    () => ({
      userType: (context) => (
        <UserTypeSelectPopover
          value={context.filters.userType || ""}
          onValueChange={(value) =>
            context.setFilters((prev) => ({ ...prev, userType: value }))
          }
          internalUserTypes={internalUserTypes}
          externalUserTypes={externalUserTypes}
          onClose={context.handleFilterMenuClose}
          onSearch={() => {
            context.handleFilterChange("userType", context.filters.userType, true);
            context.handleFilterMenuClose();
          }}
          onClear={() => {
            context.handleFilterChange("userType", "", true);
            context.handleFilterMenuClose();
          }}
        />
      ),
      createdBy: (context) => (
        <CreatedByFilterPopover
          value={context.filters.createdBy || ""}
          onValueChange={(newValue) => {
            context.handleFilterChange("createdBy", newValue, true);
            context.handleFilterMenuClose();
          }}
          onClose={context.handleFilterMenuClose}
          onClear={() => {
            context.handleFilterChange("createdBy", "", true);
            context.handleFilterMenuClose();
          }}
        />
      ),
    }),
    [externalUserTypes, internalUserTypes]
  );

  const handleCreate = useCallback(() => {
    openDialogManager("create");
  }, [openDialogManager]);

  const handleEdit = useCallback(
    (row) => {
      openDialogManager("edit", row);
    },
    [openDialogManager]
  );

  const handleExportSelected = useCallback(async () => {
    const selectedCount = selectedRows.length;

    try {
      const response = await exportProfiles(selectedCount > 0 ? selectedRows : null);

      const link = document.createElement("a");
      link.href = response.data.file;
      link.setAttribute("download", response.data.name);
      document.body.appendChild(link);
      link.click();
      link.remove();

      notify({
        open: true,
        message:
          selectedCount > 0
            ? `${selectedCount} selected users exported successfully!`
            : "All users exported successfully!",
        severity: "success",
      });

      setSelectedRows([]);
      onSortChange?.(null, null);
    } catch (error) {
      notify({
        open: true,
        message: "Failed to export users.",
        severity: "error",
      });
    }
  }, [notify, onSortChange, selectedRows, setSelectedRows]);

  const deleteRow = useCallback(
    async (row) => {
      if (!row?.originalProfile?.id) {
        throw new Error("Missing profile id for delete");
      }

      await patch(`/api/v2/profiles/${row.originalProfile.id}/`, { is_active: false });
      await usersTabData.refresh?.();

      notify({
        open: true,
        message: "User deleted successfully!",
        severity: "success",
      });
    },
    [notify, usersTabData]
  );

  const handleDeleteRequest = useCallback((row) => {
    if (!row) return;
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    if (deletingRow) return;
    setDeleteDialogOpen(false);
    setRowToDelete(null);
  }, [deletingRow]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!rowToDelete || deletingRow) return;

    setDeletingRow(true);
    try {
      await deleteRow(rowToDelete);
      setDeleteDialogOpen(false);
      setRowToDelete(null);
      setSelectedRows([]);
    } catch {
      notify({
        open: true,
        message: "Failed to delete user. Please try again.",
        severity: "error",
      });
    } finally {
      setDeletingRow(false);
    }
  }, [deleteRow, deletingRow, notify, rowToDelete, setSelectedRows]);

  const renderRowActionsMenu = useCallback(
    ({ anchorEl, onClose, row, onDeleteRequest }) => (
      <RowActionsMenu
        anchorEl={anchorEl}
        onClose={onClose}
        isUserTable
        row={row}
        onEdit={handleEdit}
        onDeleteRequest={onDeleteRequest}
      />
    ),
    [handleEdit]
  );

  const renderTopToolbar = useCallback(() => {
    const hasDate = Boolean(usersTabData.activeDateFilter);
    const hasLogin = Boolean(usersTabData.activeLoginFilter);

    if (!hasDate && !hasLogin) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, px: 3, py: 2, backgroundColor: "#f9fafb" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {hasDate && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Created Date Range:
              </Typography>
              <Chip
                label={
                  usersTabData.activeDateFilter.customStartDate && usersTabData.activeDateFilter.customEndDate
                    ? `${usersTabData.activeDateFilter.customStartDate} - ${usersTabData.activeDateFilter.customEndDate}`
                    : usersTabData.activeDateFilter.dateRange === "24h"
                    ? "Last 24 hours"
                    : usersTabData.activeDateFilter.dateRange === "7d"
                    ? "Last 7 Days"
                    : usersTabData.activeDateFilter.dateRange === "30d"
                    ? "Last 30 Days"
                    : ""
                }
                onDelete={() => {
                  usersTabData.handleDateFilterChange(null);
                  setSelectedRows([]);
                }}
                deleteIcon={<Close sx={{ fontSize: "16px !important" }} />}
              />
            </>
          )}

          {hasLogin && (
            <>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Latest Login Range:
              </Typography>
              <Chip
                label={
                  usersTabData.activeLoginFilter.customLoginStartDate && usersTabData.activeLoginFilter.customLoginEndDate
                    ? `${usersTabData.activeLoginFilter.customLoginStartDate} - ${usersTabData.activeLoginFilter.customLoginEndDate}`
                    : usersTabData.activeLoginFilter.loginRange === "24h"
                    ? "Last 24 hours"
                    : usersTabData.activeLoginFilter.loginRange === "7d"
                    ? "Last 7 Days"
                    : usersTabData.activeLoginFilter.loginRange === "30d"
                    ? "Last 30 Days"
                    : ""
                }
                onDelete={() => {
                  usersTabData.handleLoginFilterChange(null);
                  setSelectedRows([]);
                }}
                deleteIcon={<Close sx={{ fontSize: "16px !important" }} />}
              />
            </>
          )}

          <Button
            startIcon={<Delete />}
            onClick={() => {
              usersTabData.handleDateFilterChange(null);
              usersTabData.handleLoginFilterChange(null);
              setSelectedRows([]);
            }}
            size="small"
            sx={{ color: "#ef4444", textTransform: "none", fontWeight: 500 }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
    );
  }, [setSelectedRows, usersTabData]);

  const renderSelectionToolbar = useCallback(() => {
    if (!selectedRows.length) {
      return null;
    }

    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151", display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>{selectedRows.length}</Box> Users Selected
        </Typography>

        <Button
          startIcon={<EditNote />}
          onClick={() => setBulkEditDialogOpen(true)}
          size="small"
          variant="outlined"
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Bulk Edit
        </Button>

        <Button
          startIcon={<UploadFileOutlined />}
          onClick={handleExportSelected}
          size="small"
          variant="contained"
          sx={{ textTransform: "none", fontWeight: 500 }}
        >
          Export
        </Button>
      </>
    );
  }, [handleExportSelected, selectedRows.length]);

  const contract = {
    tableProps: {
      columns,
      rows: usersTabData.data,
      loading: usersTabData.loading,
      error: usersTabData.error,
      emptyMessage: "No users found",
      searchTerm: usersTabData.searchTerm,
      page: usersTabData.page,
      serverSidePagination: true,
      hasNextPage: !!usersTabData.nextCursor,
      hasPrevPage: !!usersTabData.prevCursor,
      onNextPage: usersTabData.handleNextPage,
      onPreviousPage: usersTabData.handlePreviousPage,
      onFiltersChange: usersTabData.handleFiltersChange,
      filterPopoverByField,
      onDeleteRequest: handleDeleteRequest,
      renderCellContent,
      renderRowActionsMenu,
    },
    toolbar: {
      searchValue: usersTabData.searchTerm || "",
      onSearchChange: usersTabData.handleSearchChange,
      createAction: handleCreate,
      renderTop: renderTopToolbar,
      renderSelection: renderSelectionToolbar,
    },
    dialogs: {
      render: () => (
        <>
          <UsersBulkEditDialog
            open={bulkEditDialogOpen}
            onClose={() => setBulkEditDialogOpen(false)}
            selectedUsers={usersTabData.data.filter((user) => selectedRows.includes(user.id))}
            onSubmit={() => {
              setSelectedRows([]);
              onSortChange?.(null, null);
            }}
            onShowSuccess={(message, severity = "success", autoHideDuration) =>
              notify({ open: true, message, severity, autoHideDuration })
            }
          />
          <ConfirmDeleteDialog
            open={deleteDialogOpen}
            itemLabel={rowToDelete?.name || rowToDelete?.email}
            deleting={deletingRow}
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
          />
        </>
      ),
    },
    commands: {
      refresh: usersTabData.refresh,
      exportSelected: handleExportSelected,
      clearSelection: () => setSelectedRows([]),
      deleteRow,
    },
    meta: {
      sectionId: "users",
      capabilities: {
        bulkEdit: true,
        userTypeOptions: internalUserTypes.length + externalUserTypes.length > 0,
      },
    },
  };

  assertSectionControllerContract("users", contract);
  return contract;
}
